import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getCurrentChannel, getListApiChannel, CONFIG } from '../config';
import { useAuth } from '../contexts/AuthContext';

function useFetchUsers(limit, api, options = {}) {
  const [images, setImages] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const usedUUIDs = useRef(new Set());
  const { token, isAuthenticated } = useAuth();
  // Separate pagination for unauthenticated public list
  const PUBLIC_LIMIT = 10;
  const publicOffsetRef = useRef(0);
  const { onGate } = options;
  // Ensure we always use the latest onGate passed from the component
  const onGateRef = useRef(onGate);
  const gateTriggeredRef = useRef(false); // avoid duplicate popup firing
  useEffect(() => {
    onGateRef.current = onGate;
  }, [onGate]);
  
  // Refs to keep track of current state without affecting dependency arrays
  const stateRef = useRef({
    offset: 0,
    hasMore: true,
    isLoading: false
  });
  
  // Track if initial fetch has been done
  const hasInitialFetch = useRef(false);
  
  // Update refs whenever state changes
  useEffect(() => {
    stateRef.current.offset = offset;
    stateRef.current.hasMore = hasMore;
    stateRef.current.isLoading = isLoading;
  }, [offset, hasMore, isLoading]);

  const fetchUsers = useCallback(async () => {
    // Use ref values to check conditions
    if (stateRef.current.isLoading || !stateRef.current.hasMore) {
      return;
    }

    // no console logging
    setIsLoading(true);

    try {
  // Get current channel (for UI), and list API channel (for /list endpoints)
  const uiChannel = getCurrentChannel();
  const channel = getListApiChannel();

      // Decide endpoint based on auth state
      const authed = typeof isAuthenticated === 'function' ? isAuthenticated() : !!token;

      // PUBLIC gating: trigger AFTER 2 successful fetches (changed from 3).
      // Show popup on 3rd+ fetch attempts, but CONTINUE fetching to allow scrolling
      if (!authed && publicOffsetRef.current >= (PUBLIC_LIMIT * 2)) { // 20 when limit=10 (was 30)
        if (!gateTriggeredRef.current) {
          gateTriggeredRef.current = true;
          // Entrance popup disabled; still call onGate to highlight rows if provided
          if (typeof onGateRef.current === 'function') onGateRef.current();
        }
        // REMOVED: return; - Continue fetching instead of blocking
      }

      let endpoint;
      let effectiveOffset;
      let effectiveLimit;
      if (authed) {
        effectiveOffset = stateRef.current.offset;
        effectiveLimit = limit;
        endpoint = `/list?offset=${effectiveOffset}&limit=${effectiveLimit}&channel=${channel}`;
      } else {
        effectiveOffset = publicOffsetRef.current;
        effectiveLimit = PUBLIC_LIMIT;
        endpoint = `/list/public?offset=${effectiveOffset}&limit=${effectiveLimit}&channel=${channel}`;
      }

      console.log('[Feed] Calling list API:', {
        endpoint,
        authed,
        offset: effectiveOffset,
        limit: effectiveLimit,
        channel,
        uiChannel,
      });

  const data = await api.get(endpoint);
      
      // Filter out users where is_public is false (hidden profiles)
      const publicUsers = data.users.filter(user => user.is_public !== false);
      
      const usersWithImages = publicUsers.flatMap(user =>
        user.images.map(img => ({
          uuid: user.uuid,
          imageUUID: img.uuid,
          name: user.nickname || 'Unknown',
          image: img.uuid,
          image_path: img.image_path,
          thumb_path: img.thumb_path || img.image_path, // Prefer thumbnail when available
          nickname: user.nickname || 'Anonymous',
          country: user.country || null,
          joined: new Date(user.created_at).toLocaleDateString(),
        }))
      );

      const newImages = usersWithImages.filter(img => {
        const uniqueKey = `${img.uuid}-${img.image}`;
        if (usedUUIDs.current.has(uniqueKey)) return false;
        usedUUIDs.current.add(uniqueKey);
        return true;
      });

      // Determine if API is exhausted based on response metadata
      const respUsersCount = Array.isArray(data?.users) ? data.users.length : 0;
      const respLimit = Number.isFinite(Number(data?.limit)) ? Number(data.limit) : effectiveLimit;
      const respOffset = Number.isFinite(Number(data?.offset)) ? Number(data.offset) : effectiveOffset;
      const totalCap = Number.isFinite(Number(data?.total)) ? Number(data.total)
        : (Number.isFinite(Number(data?.cap)) ? Number(data.cap) : undefined);
      const remaining = Number.isFinite(Number(data?.remaining)) ? Number(data.remaining)
        : (Number.isFinite(totalCap) ? Math.max(0, totalCap - (respOffset + respUsersCount)) : undefined);

      let exhausted = false;
      if (respUsersCount === 0) exhausted = true; // no items returned
      if (!exhausted && Number.isFinite(remaining) && remaining <= 0) exhausted = true; // explicit remaining
      if (!exhausted && Number.isFinite(totalCap) && (respOffset + respUsersCount) >= totalCap) exhausted = true; // reached total
      if (!exhausted && respUsersCount < respLimit) exhausted = true; // short page implies end

      if (exhausted) {
        setHasMore(false);
      }
      // no console logging
  setImages(prev => [...prev, ...newImages]);
      if (authed) {
        setOffset(prev => prev + limit);
      } else {
        publicOffsetRef.current = publicOffsetRef.current + PUBLIC_LIMIT; // advance for next potential gate check
      }

  // No additional counters needed; publicOffsetRef drives gating
    } catch (err) {
      // no console logging
      // Error notification is already handled by CustomFetchApi
    } finally {
      setIsLoading(false);
    }
  }, [api, limit, token, isAuthenticated]); // Re-evaluate when auth state changes

  // Initial fetch (skip during prerender)
  useEffect(() => {
    if (typeof navigator !== 'undefined' && /HeadlessChrome/.test(navigator.userAgent)) return;
    if (hasInitialFetch.current) return; // Only fetch once
    hasInitialFetch.current = true;
    fetchUsers();
  }, []); // Empty dependency array to run only once

  const prependImage = useCallback((newImage) => {
    setImages(prev => {
      // Check if the image already exists in the last 10 images
      const last10Images = prev.slice(0, 10);
      const imageExists = last10Images.some(img => img.uuid === newImage.uuid);
      
      if (imageExists) {
        return prev; // Don't add duplicate
      }
      
      const uniqueKey = `${newImage.uuid}-${newImage.image}`;
      usedUUIDs.current.add(uniqueKey);
      // no console logging
      return [newImage, ...prev];
    });
  }, []);

  const updateImagePaths = useCallback((userUUID, updatedImages) => {
    setImages(prev => {
      return prev.map(img => {
        // Find matching image by user UUID
        if (img.uuid === userUUID && updatedImages.length > 0) {
          // Find the corresponding updated image
          const updatedImage = updatedImages.find(updated => updated.uuid === img.imageUUID);
          if (updatedImage) {
            return {
              ...img,
              image_path: updatedImage.image_path,
              thumb_path: updatedImage.thumb_path || updatedImage.image_path, // Prefer thumbnail when available
            };
          }
        }
        return img;
      });
    });
  }, []);

  return { images, fetchUsers, isLoading, hasMore, prependImage, updateImagePaths };
}

export default useFetchUsers;